import React, { useState } from 'react';
import { ArrowRight, Check, Layers, Cpu, TrendingUp, Sparkles, Code2, Database, Workflow, Search, Globe, ChevronRight } from 'lucide-react';
import { GROWTH_PILLARS, Pillar } from '../data/siteData';

interface PillarsSectionProps {
  onSelectSolution: (pillarId: string, itemName?: string) => void;
  onOpenBooking: () => void;
}

export const PillarsSection: React.FC<PillarsSectionProps> = ({ onSelectSolution, onOpenBooking }) => {
  const [selectedPillarId, setSelectedPillarId] = useState<string>('infrastructure');
  const [showDetailedBreakdown, setShowDetailedBreakdown] = useState<boolean>(true);

  const selectedPillar = GROWTH_PILLARS.find((p) => p.id === selectedPillarId) || GROWTH_PILLARS[0];

  return (
    <section id="solutions" className="py-20 lg:py-28 bg-slate-50 border-b border-slate-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div className="max-w-2xl text-left">
            <div className="text-xs font-bold uppercase tracking-widest text-[#b88e4c] mb-2">
              What we build
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
              Systems across the <br className="hidden sm:block" />
              growth stack.
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed font-normal">
              We build infrastructure — not deliverables. Three pillars, one operating system for the next decade of African business.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => onSelectSolution('all')}
              className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 hover:text-[#b88e4c] transition-colors group"
            >
              <span>Explore all solutions</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>

        {/* 3 Pillars Selector Tabs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {GROWTH_PILLARS.map((pillar) => {
            const isSelected = pillar.id === selectedPillarId;
            return (
              <button
                key={pillar.id}
                onClick={() => setSelectedPillarId(pillar.id)}
                className={`text-left p-6 rounded-2xl transition-all duration-200 cursor-pointer border ${
                  isSelected
                    ? 'bg-white border-[#b88e4c] shadow-md ring-1 ring-emerald-500/20'
                    : 'bg-white/60 border-slate-200 hover:bg-white hover:border-slate-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-bold tracking-wider uppercase ${isSelected ? 'text-[#8d6521]' : 'text-slate-600'}`}>
                    {pillar.number}
                  </span>
                  {isSelected && (
                    <span className="h-2 w-2 rounded-full bg-[#8d6521]" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-slate-900 font-display mb-1">
                  {pillar.title}
                </h3>
                <p className="text-xs sm:text-sm text-slate-600 line-clamp-2">
                  {pillar.subtitle}
                </p>
              </button>
            );
          })}
        </div>

        {/* View More Architecture Details Dropdown Bar */}
        <div className="mb-6 flex justify-center">
          <button
            onClick={() => setShowDetailedBreakdown(!showDetailedBreakdown)}
            className="px-5 py-3 rounded-xl bg-white hover:bg-slate-100 text-slate-800 border border-slate-300 text-xs font-bold flex items-center gap-2 cursor-pointer transition-colors shadow-xs min-h-[44px]"
          >
            <span>{showDetailedBreakdown ? 'Hide Architecture Breakdown' : 'View More Architecture Breakdown & Tech Stack'}</span>
            <span className="text-[#8d6521] font-bold">{showDetailedBreakdown ? '▲' : '▼'}</span>
          </button>
        </div>

        {/* Active Pillar Detailed Breakdown */}
        {showDetailedBreakdown && (
        <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-10 shadow-xs animate-in fade-in duration-200">
          <div className="max-w-3xl mb-10 text-left">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-[#b88e4c] bg-emerald-50 px-3 py-1 rounded-full mb-3">
              <span>{selectedPillar.number} Focus</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display">
              {selectedPillar.subtitle}
            </h3>
            <p className="mt-2 text-slate-600 text-sm sm:text-base leading-relaxed">
              {selectedPillar.tagline}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {selectedPillar.items.map((item, idx) => (
              <div
                key={item.name}
                className="flex flex-col justify-between rounded-2xl bg-slate-50 border border-slate-200/80 p-6 hover:border-[#b88e4c]/50 hover:bg-emerald-50/20 transition-all text-left group"
              >
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-xs font-mono font-bold text-slate-600">
                      0{idx + 1}
                    </span>
                    <span className="h-8 w-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-700 group-hover:text-[#8d6521] group-hover:border-amber-200 transition-colors">
                      {idx === 0 ? <Globe className="h-4 w-4" /> : idx === 1 ? <Cpu className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-slate-900 font-display mb-2 group-hover:text-amber-900 transition-colors">
                    {item.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-700 leading-relaxed mb-6">
                    {item.description}
                  </p>

                  <div className="space-y-2 mb-6">
                    {item.features.map((feat) => (
                      <div key={feat} className="flex items-start gap-2 text-xs text-slate-800">
                        <Check className="h-3.5 w-3.5 text-[#8d6521] shrink-0 mt-0.5" />
                        <span>{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-200/60 flex items-center justify-between">
                  <button
                    onClick={() => onSelectSolution(selectedPillar.id, item.name)}
                    className="text-xs font-bold text-slate-900 hover:text-[#8d6521] inline-flex items-center gap-1 group-hover:underline min-h-[36px]"
                  >
                    <span>Architecture details</span>
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={onOpenBooking}
                    className="text-xs font-bold text-[#8d6521] hover:text-amber-950 min-h-[36px]"
                  >
                    Deploy this →
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Consultation Banner inside Pillars */}
          <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 bg-slate-50/90 p-4 sm:p-5 rounded-2xl text-left border border-slate-200">
            <div>
              <p className="text-sm font-bold text-slate-900">Need a hybrid architecture across multiple pillars?</p>
              <p className="text-xs text-slate-600 mt-0.5">We formulate integrated tech stacks combining custom web apps, WhatsApp automation, and technical SEO.</p>
            </div>
            <button
              onClick={onOpenBooking}
              className="px-5 py-3 text-xs font-bold text-white bg-[#080d11] hover:bg-slate-800 rounded-xl whitespace-nowrap cursor-pointer transition-colors min-h-[44px]"
            >
              Book Architecture Review
            </button>
          </div>
        </div>
        )}

      </div>
    </section>
  );
};
